import React, { useState } from 'react'
import { Tooltip, Typography, withStyles } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { clearCardState, getCardAction } from 'src/reducers/card/action'
import { courseGetAction } from 'src/reducers/course/action'
import {
  CardPublishState,
  useCoursePublish,
  usePagePublishState,
} from 'src/contexts/course-publish-context'
import styles from './styles'

/**
 * Whether the completion page is live, and what to do about it if it is not.
 *
 * The table of contents carries this marker for every other page, but the completion page has no
 * row there — so the one page you cannot reach from the outline was also the one page whose state
 * you could not see while editing it.
 */
const CompletionPublishState = props => {
  const { classes, cardId } = props
  const dispatch = useDispatch()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const publishState = useCoursePublish()
  const pageState = usePagePublishState(cardId)
  const [busy, setBusy] = useState(false)

  // While the whole course is a draft, per-page state is meaningless and actively misleading:
  // publishing this page would not make anyone able to see it. A live and up-to-date page says
  // nothing either, the same way its row in the outline stays bare.
  const pending =
    Boolean(publishState?.isPublished) &&
    pageState !== null &&
    pageState.state !== CardPublishState.PUBLISHED

  if (!pending) {
    return null
  }

  const isDraft = pageState.state === CardPublishState.DRAFT

  const run = async (action, successMessage, restoreContent) => {
    if (busy) {
      return
    }

    setBusy(true)

    try {
      if (restoreContent) {
        // Tear the editor down BEFORE discarding. It flushes any pending autosave when it is
        // destroyed, so unmounting afterwards would write the very edits we just threw away
        // straight back — discard would appear to do nothing.
        dispatch(clearCardState())
      }

      await action()

      if (restoreContent) {
        dispatch(courseGetAction(id))
        dispatch(getCardAction(cardId))
      }

      enqueueSnackbar(successMessage, { variant: 'success' })
    } catch (error) {
      if (restoreContent) {
        // The editor is gone at this point, so put the page back rather than leaving a blank pane.
        dispatch(getCardAction(cardId))
      }

      enqueueSnackbar('Could not update the completion page.', {
        variant: 'error',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={classes.root}>
      <Tooltip
        title={
          isDraft
            ? 'Learners do not see a completion page yet'
            : 'Learners still see the previous completion page'
        }
        placement="top"
      >
        <span className={isDraft ? classes.draftBadge : classes.editedBadge}>
          {isDraft ? 'Draft' : 'Edited'}
        </span>
      </Tooltip>

      <span
        className={classes.action}
        onClick={() =>
          run(
            () => publishState.publishPage(cardId),
            isDraft ? 'Completion page published.' : 'Changes published.',
          )
        }
      >
        <Icon icon="mdi:tray-arrow-up" fontSize={14} />
        <Typography className={classes.actionText}>
          {isDraft ? 'Publish page' : 'Publish changes'}
        </Typography>
      </span>

      {!isDraft && (
        <span
          className={classes.action}
          onClick={() =>
            run(
              () => publishState.discardPageChanges(cardId),
              'Changes discarded.',
              true,
            )
          }
        >
          <Icon icon="mdi:undo-variant" fontSize={14} />
          <Typography className={classes.actionText}>
            Discard changes
          </Typography>
        </span>
      )}
    </div>
  )
}

export default withStyles(styles)(CompletionPublishState)
